/**
 * <pdf-flipbook>
 *
 * Muestra un PDF como revista: PDF.js pinta cada pagina y StPageFlip hace el
 * paso de hoja. Los links del PDF se recrean como <a> transparentes encima de
 * cada pagina, en porcentajes, para que escalen con el libro.
 *
 * Las librerias se cargan con import() solo cuando el visor se acerca a la
 * pantalla, asi no pesan en el resto de la pagina. Las rutas llegan por
 * data-attributes desde la seccion (asset_url).
 *
 * Notas sobre StPageFlip (assets/vendor-page-flip.module.js):
 * - La orientacion la decide la libreria con `blockWidth < minWidth * 2`.
 *   Para forzar una sola pagina subimos minWidth en caliente (el objeto de
 *   settings es compartido con el render) y anulamos por CSS el min-width que
 *   la libreria escribe inline en el contenedor.
 * - El loop de requestAnimationFrame esta parchado para respetar
 *   `render.paused` y `render.destroyed`.
 */

const ZOOM_LEVELS = [1, 1.5, 2, 3];
const MAX_RENDER_WIDTH = 3000;
const PREFETCH_AHEAD = 4;
const PREFETCH_BEHIND = 2;
const KEEP_RENDERED = 8;
const FORCE_SINGLE_MIN_WIDTH = 1e6;

const absoluteUrl = (url) => new URL(url, window.location.href).href;

let librariesPromise = null;

function loadLibraries({ pdfjs, worker, pageFlip }) {
  if (!librariesPromise) {
    librariesPromise = Promise.all([import(absoluteUrl(pdfjs)), import(absoluteUrl(pageFlip))]).then(
      ([pdfjsLib, pageFlipLib]) => {
        pdfjsLib.GlobalWorkerOptions.workerSrc = absoluteUrl(worker);
        return { pdfjsLib, PageFlip: pageFlipLib.PageFlip };
      }
    );
    librariesPromise.catch(() => {
      librariesPromise = null;
    });
  }
  return librariesPromise;
}

class PdfFlipbook extends HTMLElement {
  connectedCallback() {
    if (this.teardownTimer) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }
    if (this.started) return;
    this.started = true;

    this.viewport = this.querySelector('[data-viewport]');
    this.stage = this.querySelector('[data-stage]');
    this.book = this.querySelector('[data-book]');
    this.toolbar = this.querySelector('[data-toolbar]');
    this.counter = this.querySelector('[data-counter]');
    this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');

    const data = this.dataset;
    this.options = {
      src: data.src,
      mode: data.mode === 'spread' ? 'spread' : 'single',
      breakpoint: parseInt(data.breakpoint, 10) || 900,
      showCover: data.showCover === 'true',
      maxHeight: parseFloat(data.maxHeight) || 85,
      flippingTime: parseInt(data.flippingTime, 10) || 800,
      shadow: data.shadow !== 'false',
      corners: data.corners !== 'false',
      cornerSize: parseInt(data.cornerSize, 10) || 80,
      newTab: data.newTab === 'true',
      deepLink: data.deepLink === 'true',
    };

    this.zoom = 1;
    this.inView = false;
    this.layoutKey = '';

    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.lazyObserver.disconnect();
          this.init();
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.lazyObserver.observe(this);
  }

  disconnectedCallback() {
    // El editor de temas mueve secciones desconectando y reconectando el nodo;
    // solo se desmonta si de verdad salio del documento.
    this.teardownTimer = setTimeout(() => {
      if (!this.isConnected) this.teardown();
    }, 0);
  }

  async init() {
    this.setStatus('loading');

    try {
      const { pdfjsLib, PageFlip } = await loadLibraries({
        pdfjs: this.dataset.pdfjs,
        worker: this.dataset.pdfjsWorker,
        pageFlip: this.dataset.pageFlip,
      });
      if (this.destroyed) return;

      this.loadingTask = pdfjsLib.getDocument({
        url: absoluteUrl(this.options.src),
        disableAutoFetch: true,
        disableStream: true,
        rangeChunkSize: 262144,
      });
      this.pdf = await this.loadingTask.promise;
      if (this.destroyed) return;

      const firstPage = await this.pdf.getPage(1);
      const { width, height } = firstPage.getViewport({ scale: 1 });
      this.pageRatio = width / height;
      this.pageCount = this.pdf.numPages;

      this.buildPages();
      this.layout(true);

      this.pageFlip = new PageFlip(this.book, {
        width: Math.round(width),
        height: Math.round(height),
        size: 'stretch',
        minWidth: this.isSpread ? 1 : FORCE_SINGLE_MIN_WIDTH,
        maxWidth: FORCE_SINGLE_MIN_WIDTH * 10,
        minHeight: 1,
        maxHeight: FORCE_SINGLE_MIN_WIDTH * 10,
        startPage: this.initialPage(),
        showCover: this.options.showCover,
        drawShadow: this.options.shadow,
        maxShadowOpacity: 0.5,
        flippingTime: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 300 : this.options.flippingTime,
        usePortrait: true,
        autoSize: true,
        mobileScrollSupport: true,
        clickEventForward: true,
        showPageCorners: this.options.corners,
        // Zona activa de la esquina en px (parche en vendor-page-flip.module.js).
        cornerSize: this.options.cornerSize,
      });
      this.pageFlip.loadFromHTML(this.pages.map((page) => page.el));
      this.pageFlip.on('flip', () => this.onPageChange(true));
      this.pageFlip.on('changeOrientation', () => this.onPageChange(false));

      this.bindEvents();
      this.layout(true);
      this.setStatus('ready');
      this.onPageChange(false);
    } catch (error) {
      if (this.destroyed) return;
      console.error('[pdf-flipbook]', error);
      this.setStatus('error');
    }
  }

  /* ---------------------------------------------------------------- Pages */

  buildPages() {
    this.pages = Array.from({ length: this.pageCount }, (_, index) => {
      const el = document.createElement('div');
      el.className = 'pdf-flipbook__page';
      el.setAttribute('role', 'group');
      el.setAttribute('aria-label', `${this.strings.page || 'Page'} ${index + 1}`);

      const img = document.createElement('img');
      img.className = 'pdf-flipbook__image';
      img.alt = '';
      img.draggable = false;
      img.decoding = 'async';

      const links = document.createElement('div');
      links.className = 'pdf-flipbook__links';

      el.append(img, links);
      return { el, img, links, renderedWidth: 0, objectUrl: null, linksReady: false };
    });

    // Misma reparticion que PageCollection.createSpread() de StPageFlip.
    this.landscapeSpreads = [];
    let start = 0;
    if (this.options.showCover) {
      this.landscapeSpreads.push([0]);
      start = 1;
    }
    for (let i = start; i < this.pageCount; i += 2) {
      this.landscapeSpreads.push(i < this.pageCount - 1 ? [i, i + 1] : [i]);
    }
  }

  visiblePages() {
    if (!this.pageFlip) return [0];
    const index = this.pageFlip.getCurrentPageIndex();
    if (this.pageFlip.getOrientation() !== 'landscape') return [index];
    const spread = this.landscapeSpreads[this.pageFlip.getPageCollection().getCurrentSpreadIndex()];
    return spread || [index];
  }

  renderOrder() {
    const visible = this.visiblePages();
    const first = Math.min(...visible);
    const last = Math.max(...visible);
    const order = [...visible];
    for (let d = 1; d <= PREFETCH_AHEAD; d++) order.push(last + d);
    for (let d = 1; d <= PREFETCH_BEHIND; d++) order.push(first - d);
    return order.filter((i, pos) => i >= 0 && i < this.pageCount && order.indexOf(i) === pos);
  }

  targetRenderWidth() {
    const cssWidth = this.pageFlip?.getBoundsRect()?.pageWidth || this.stage.offsetWidth;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.ceil((cssWidth * dpr) / 256) * 256;
    return Math.max(512, Math.min(MAX_RENDER_WIDTH, width));
  }

  scheduleRender() {
    if (this.renderFrame) return;
    this.renderFrame = requestAnimationFrame(() => {
      this.renderFrame = null;
      this.pumpRenderQueue();
    });
  }

  async pumpRenderQueue() {
    if (this.rendering) {
      this.renderAgain = true;
      return;
    }
    this.rendering = true;
    try {
      do {
        this.renderAgain = false;
        const target = this.targetRenderWidth();
        for (const index of this.renderOrder()) {
          if (this.destroyed || this.renderAgain) break;
          if (this.pages[index].renderedWidth >= target * 0.9) continue;
          await this.renderPage(index, target);
        }
        if (!this.destroyed) this.releaseFarPages();
      } while (this.renderAgain && !this.destroyed);
    } catch (error) {
      if (!this.destroyed) console.error('[pdf-flipbook]', error);
    } finally {
      this.rendering = false;
    }
  }

  async renderPage(index, targetWidth) {
    const page = this.pages[index];
    const pdfPage = await this.pdf.getPage(index + 1);
    const base = pdfPage.getViewport({ scale: 1 });

    if (!page.linksReady) {
      page.linksReady = true;
      await this.addLinks(page, pdfPage, base).catch((error) => console.warn('[pdf-flipbook]', error));
    }

    const viewport = pdfPage.getViewport({ scale: targetWidth / base.width });
    const canvas = this.canvas || (this.canvas = document.createElement('canvas'));
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    this.renderTask = pdfPage.render({ canvas, viewport, background: '#ffffff' });
    await this.renderTask.promise;
    this.renderTask = null;

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    pdfPage.cleanup();
    if (!blob || this.destroyed) return;

    const url = URL.createObjectURL(blob);
    page.img.src = url;
    try {
      await page.img.decode();
    } catch (error) {
      // decode() falla si la imagen se reemplaza mientras decodifica; no importa.
    }
    if (page.objectUrl) URL.revokeObjectURL(page.objectUrl);
    page.objectUrl = url;
    page.renderedWidth = targetWidth;
    page.el.classList.add('is-rendered');
  }

  releaseFarPages() {
    const visible = this.visiblePages();
    const first = Math.min(...visible) - KEEP_RENDERED;
    const last = Math.max(...visible) + KEEP_RENDERED;
    this.pages.forEach((page, index) => {
      if (!page.objectUrl || (index >= first && index <= last)) return;
      page.img.removeAttribute('src');
      URL.revokeObjectURL(page.objectUrl);
      page.objectUrl = null;
      page.renderedWidth = 0;
      page.el.classList.remove('is-rendered');
    });
  }

  /* ---------------------------------------------------------------- Links */

  async addLinks(page, pdfPage, base) {
    const annotations = await pdfPage.getAnnotations({ intent: 'display' });

    for (const annotation of annotations) {
      if (annotation.subtype !== 'Link' || !annotation.rect) continue;

      // `url` solo viene con protocolos seguros (http, https, mailto, tel).
      const href = annotation.url || null;
      const targetPage = href ? null : await this.resolveDestination(annotation.dest);
      if (!href && targetPage === null) continue;

      const [x1, y1] = base.convertToViewportPoint(annotation.rect[0], annotation.rect[1]);
      const [x2, y2] = base.convertToViewportPoint(annotation.rect[2], annotation.rect[3]);
      const link = document.createElement('a');
      link.className = 'pdf-flipbook__link';
      link.style.left = `${(Math.min(x1, x2) / base.width) * 100}%`;
      link.style.top = `${(Math.min(y1, y2) / base.height) * 100}%`;
      link.style.width = `${(Math.abs(x2 - x1) / base.width) * 100}%`;
      link.style.height = `${(Math.abs(y2 - y1) / base.height) * 100}%`;

      if (href) {
        link.href = href;
        link.setAttribute('aria-label', this.linkLabel(href));
        if (this.options.newTab) {
          link.target = '_blank';
          link.rel = 'noopener';
        }
      } else {
        link.href = `#p=${targetPage + 1}`;
        link.dataset.page = targetPage;
        link.setAttribute('aria-label', `${this.strings.goToPage || 'Go to page'} ${targetPage + 1}`);
      }
      page.links.append(link);
    }
  }

  isOverLink(x, y, margin = 8) {
    return this.visiblePages().some((index) =>
      Array.from(this.pages[index].links.children).some((link) => {
        const rect = link.getBoundingClientRect();
        return (
          x >= rect.left - margin && x <= rect.right + margin && y >= rect.top - margin && y <= rect.bottom + margin
        );
      })
    );
  }

  async resolveDestination(dest) {
    if (!dest) return null;
    try {
      const explicit = typeof dest === 'string' ? await this.pdf.getDestination(dest) : dest;
      if (!Array.isArray(explicit)) return null;
      const ref = explicit[0];
      const index = typeof ref === 'object' && ref !== null ? await this.pdf.getPageIndex(ref) : ref;
      return Number.isInteger(index) && index >= 0 && index < this.pageCount ? index : null;
    } catch (error) {
      return null;
    }
  }

  linkLabel(href) {
    try {
      const url = new URL(href);
      const slug = url.pathname.split('/').filter(Boolean).pop();
      return slug ? decodeURIComponent(slug).replace(/[-_]+/g, ' ') : url.hostname;
    } catch (error) {
      return href;
    }
  }

  /* --------------------------------------------------------------- Layout */

  layout(force = false) {
    const available = this.viewport.offsetWidth;
    if (!available) return;

    const fullscreen = this.classList.contains('is-fullscreen');
    const toolbarHeight = this.toolbar ? this.toolbar.offsetHeight : 0;
    const viewportPadding = fullscreen ? parseFloat(getComputedStyle(this).paddingTop) * 2 : 0;
    const maxHeight = fullscreen
      ? window.innerHeight - toolbarHeight - viewportPadding
      : (window.innerHeight * this.options.maxHeight) / 100;

    const key = `${available}|${maxHeight}|${this.zoom}|${fullscreen}`;
    if (!force && key === this.layoutKey) return;
    this.layoutKey = key;

    const ratio = this.pageRatio || 842 / 596;
    this.isSpread = this.options.mode === 'spread' && available >= this.options.breakpoint;
    const aspect = ratio * (this.isSpread ? 2 : 1);
    const fitWidth = Math.max(120, Math.floor(Math.min(available, maxHeight * aspect)));
    const fitHeight = Math.round(fitWidth / aspect);

    this.stage.style.width = `${Math.round(fitWidth * this.zoom)}px`;
    this.viewport.style.height = this.zoom > 1 ? `${fitHeight}px` : '';

    if (this.pageFlip) {
      this.pageFlip.getSettings().minWidth = this.isSpread ? 1 : FORCE_SINGLE_MIN_WIDTH;
      this.pageFlip.update();
      this.scheduleRender();
    }
  }

  requestLayout() {
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = requestAnimationFrame(() => this.layout());
  }

  /* --------------------------------------------------------------- Events */

  bindEvents() {
    this.onClick = (event) => {
      const control = event.target.closest('[data-action]');
      if (control && this.contains(control)) {
        const action = control.dataset.action;
        if (action === 'prev') this.pageFlip.flipPrev();
        if (action === 'next') this.pageFlip.flipNext();
        if (action === 'zoom-in') this.stepZoom(1);
        if (action === 'zoom-out') this.stepZoom(-1);
        if (action === 'fullscreen') this.toggleFullscreen();
        return;
      }
      const internal = event.target.closest('a[data-page]');
      if (internal && this.book.contains(internal)) {
        event.preventDefault();
        this.goTo(parseInt(internal.dataset.page, 10));
      }
    };
    this.addEventListener('click', this.onClick);

    this.onKeydown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const fullscreen = this.classList.contains('is-fullscreen');
      if (!fullscreen && !this.inView && !this.contains(document.activeElement)) return;

      if (event.key === 'ArrowRight') {
        this.pageFlip.flipNext();
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        this.pageFlip.flipPrev();
        event.preventDefault();
      } else if (event.key === 'Escape' && this.classList.contains('is-fullscreen-fallback')) {
        this.setFullscreenState(false);
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    // Con zoom, arrastrar mueve la vista en lugar de pasar la hoja: cortamos el
    // evento en captura antes de que llegue al libro.
    this.onPanStart = (event) => {
      if (this.zoom <= 1 || event.target.closest('a, button')) return;
      event.stopPropagation();
      if (event.type !== 'mousedown') return; // en touch el scroll nativo hace el paneo

      event.preventDefault();
      const viewport = this.viewport;
      const start = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
      this.classList.add('is-panning');
      const move = (moveEvent) => {
        viewport.scrollLeft = start.left - (moveEvent.clientX - start.x);
        viewport.scrollTop = start.top - (moveEvent.clientY - start.y);
      };
      const end = () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', end);
        this.classList.remove('is-panning');
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', end);
    };
    // La esquina doblada se dibuja encima de la pagina y tapa los links que
    // quedan cerca de la orilla. Sobre un link se apaga y se recoge; este
    // listener corre antes que el de StPageFlip, que escucha en window.
    this.onHoverMove = (event) => {
      if (!this.options.corners || this.zoom > 1) return;
      const settings = this.pageFlip.getSettings();
      const overLink = this.isOverLink(event.clientX, event.clientY);
      if (settings.showPageCorners === !overLink) return;
      settings.showPageCorners = !overLink;
      if (overLink && this.pageFlip.getState() === 'fold_corner') {
        this.pageFlip.getFlipController().showCorner({ x: -1, y: -1 });
      }
    };
    this.viewport.addEventListener('mousemove', this.onHoverMove);

    this.viewport.addEventListener('mousedown', this.onPanStart, true);
    this.viewport.addEventListener('touchstart', this.onPanStart, { capture: true, passive: true });

    this.onFullscreenChange = () => {
      const element = document.fullscreenElement || document.webkitFullscreenElement;
      if (element === this) this.setFullscreenState(true);
      else if (this.classList.contains('is-fullscreen') && !this.classList.contains('is-fullscreen-fallback')) {
        this.setFullscreenState(false);
      }
    };
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);

    this.onWindowResize = () => this.requestLayout();
    window.addEventListener('resize', this.onWindowResize);
    this.resizeObserver = new ResizeObserver(() => this.requestLayout());
    this.resizeObserver.observe(this);

    // Fuera de pantalla se pausa el loop de StPageFlip, que redibuja cada frame.
    this.viewObserver = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.intersectionRatio >= 0.5;
        const render = this.pageFlip?.getRender();
        if (render) render.paused = !entry.isIntersecting;
      },
      { threshold: [0, 0.5] }
    );
    this.viewObserver.observe(this);
  }

  onPageChange(fromUser) {
    const visible = this.visiblePages();
    const first = Math.min(...visible);
    const last = Math.max(...visible);

    if (this.counter) {
      const current = first === last ? `${first + 1}` : `${first + 1}–${last + 1}`;
      this.counter.textContent = `${current} / ${this.pageCount}`;
    }
    this.querySelectorAll('[data-action="prev"]').forEach((button) => (button.disabled = first <= 0));
    this.querySelectorAll('[data-action="next"]').forEach(
      (button) => (button.disabled = last >= this.pageCount - 1)
    );

    // StPageFlip tambien emite 'flip' al montar; solo un cambio real toca la URL.
    const changed = this.lastFirstPage !== undefined && first !== this.lastFirstPage;
    this.lastFirstPage = first;
    if (fromUser && changed && this.ownsHash()) {
      const url = `${window.location.pathname}${window.location.search}#p=${first + 1}`;
      window.history.replaceState(window.history.state, '', url);
    }

    this.scheduleRender();
  }

  goTo(index) {
    if (!this.pageFlip || !Number.isInteger(index)) return;
    const target = Math.max(0, Math.min(this.pageCount - 1, index));
    const visible = this.visiblePages();
    const distance = Math.min(...visible.map((i) => Math.abs(i - target)));
    if (distance <= 2) this.pageFlip.flip(target);
    else this.pageFlip.turnToPage(target);
    this.onPageChange(true);
  }

  ownsHash() {
    return this.options.deepLink && document.querySelector('pdf-flipbook[data-deep-link="true"]') === this;
  }

  initialPage() {
    if (!this.ownsHash()) return 0;
    const match = window.location.hash.match(/(?:^#|&)p=(\d+)/);
    const page = match ? parseInt(match[1], 10) - 1 : 0;
    return Math.max(0, Math.min(this.pageCount - 1, page));
  }

  /* ----------------------------------------------------- Zoom & fullscreen */

  stepZoom(direction) {
    const index = ZOOM_LEVELS.indexOf(this.zoom);
    const next = ZOOM_LEVELS[Math.max(0, Math.min(ZOOM_LEVELS.length - 1, index + direction))];
    this.setZoom(next);
  }

  setZoom(level) {
    if (level === this.zoom || !this.pageFlip) return;
    const viewport = this.viewport;
    const centerX = (viewport.scrollLeft + viewport.clientWidth / 2) / (viewport.scrollWidth || 1);
    const centerY = (viewport.scrollTop + viewport.clientHeight / 2) / (viewport.scrollHeight || 1);

    this.zoom = level;
    this.classList.toggle('is-zoomed', level > 1);
    this.pageFlip.getSettings().showPageCorners = level === 1 && this.options.corners;
    this.layout(true);

    viewport.scrollLeft = centerX * viewport.scrollWidth - viewport.clientWidth / 2;
    viewport.scrollTop = centerY * viewport.scrollHeight - viewport.clientHeight / 2;

    this.querySelectorAll('[data-action="zoom-out"]').forEach((button) => (button.disabled = level <= ZOOM_LEVELS[0]));
    this.querySelectorAll('[data-action="zoom-in"]').forEach(
      (button) => (button.disabled = level >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1])
    );
  }

  toggleFullscreen() {
    const active = this.classList.contains('is-fullscreen');
    const element = document.fullscreenElement || document.webkitFullscreenElement;

    if (active) {
      if (element === this) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      else this.setFullscreenState(false);
      return;
    }

    const request = this.requestFullscreen || this.webkitRequestFullscreen;
    const enabled = document.fullscreenEnabled || document.webkitFullscreenEnabled;
    if (request && enabled) {
      // Safari viejo no devuelve promesa.
      Promise.resolve(request.call(this)).catch(() => this.setFullscreenState(true, true));
    } else {
      // iPhone no deja poner en pantalla completa un elemento que no sea video.
      this.setFullscreenState(true, true);
    }
  }

  setFullscreenState(active, fallback = false) {
    this.classList.toggle('is-fullscreen', active);
    this.classList.toggle('is-fullscreen-fallback', active && fallback);
    document.documentElement.classList.toggle('pdf-flipbook-lock', active && fallback);
    this.querySelectorAll('[data-action="fullscreen"]').forEach((button) => {
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? this.strings.exitFullscreen : this.strings.fullscreen);
    });
    if (!active && this.zoom > 1) this.setZoom(1);
    this.requestLayout();
  }

  /* --------------------------------------------------------------- Status */

  setStatus(status) {
    this.dataset.status = status;
    this.classList.toggle('is-ready', status === 'ready');
  }

  teardown() {
    this.destroyed = true;
    this.lazyObserver?.disconnect();
    this.viewObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.layoutFrame);
    cancelAnimationFrame(this.renderFrame);

    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    if (this.onFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    }
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    if (this.classList.contains('is-fullscreen-fallback')) {
      document.documentElement.classList.remove('pdf-flipbook-lock');
    }

    if (this.pageFlip) {
      const render = this.pageFlip.getRender();
      if (render) render.destroyed = true;
      try {
        this.pageFlip.destroy();
      } catch (error) {
        // El nodo ya pudo haber salido del documento.
      }
      this.pageFlip = null;
    }

    this.renderTask?.cancel();
    this.loadingTask?.destroy();
    this.pdf = null;
    this.pages?.forEach((page) => page.objectUrl && URL.revokeObjectURL(page.objectUrl));
  }
}

if (!customElements.get('pdf-flipbook')) customElements.define('pdf-flipbook', PdfFlipbook);
